import { validateMaterialScopeLimitations } from '../supplyChainMaterialScope';
import { computeDataCenterFinance, isDataCenterFinance } from '../dataCenterFinance';
import type { ReportTypeId } from '../../config/reportTypes';
import type {
  FinancialAssumptions,
  FinancialAssumptionsCore,
} from '../../types/financialAssumptions';
import type { SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import type { SupplyChainPlan } from '../../types/supplyChain';
import { canonicalCore } from '../financialScenarios';
import {
  computeCashFlowsUsd,
  deriveWaccPct,
  recompute,
  type RecomputeResult,
} from '../projectFinance';
import {
  SUPPLY_CHAIN_MIN_REPORTABLE_BOM_ITEMS,
  isReportableUserBomItem,
  isSupplyChainPlanUserReady,
} from '../supplyChainPlanReady';
import { resolvePromotedGenerationScenario } from '../generationImpact/promotedScenario';
import { assessGenerationImpactTestFitApplicability } from '../generationImpact/testFitApplicability';
import { selectedGridLedPlanningReadinessReason } from '../generationImpact/gridLedStandbyPlanningReadiness';

export type ReportAuthorityResolver = 'financial-model' | 'equipment-bom' | 'generation-scenario';

export type ReportAuthorityBlockerCode =
  | 'financial-model-missing'
  | 'financial-model-incomplete'
  | 'supply-chain-bom-missing'
  // STAGE 5.2 — the Generation & Impact report is the power consequence of a
  // SOLVED, PROMOTED generation scenario. Same shape as
  // `financial-model-missing`: a named blocker whose resolver names the surface
  // that clears it.
  | 'generation-scenario-missing'
  | 'generation-scenario-stale'
  // A GRID-ONLY site never promotes a scenario, so this report does not apply
  // to it at all. Distinct from the two above, which are "not yet".
  | 'generation-not-applicable';

export type ReportAuthorityWarningCode =
  | 'supply-chain-suppliers-missing'
  | 'supply-chain-pricing-incomplete'
  | 'supply-chain-quotes-missing'
  | 'supply-chain-scope-incomplete'
  | 'generation-planning-scope';

type SupplyChainAuthorityWarningCode =
  NonNullable<SiteRagReport['supplyChainAuthorityAtGen']>['warningCodes'][number];

export interface ReportAuthorityFinding<
  Code extends string = ReportAuthorityBlockerCode | ReportAuthorityWarningCode,
> {
  code: Code;
  message: string;
  resolver: ReportAuthorityResolver;
  fields?: string[];
}

export interface ReportAuthorityReadiness {
  ready: boolean;
  blockers: Array<ReportAuthorityFinding<ReportAuthorityBlockerCode>>;
  warnings: Array<ReportAuthorityFinding<ReportAuthorityWarningCode>>;
  financialMetrics?: RecomputeResult;
  financialDecisionMetrics?: FinancialDecisionMetricAvailability;
}

export type FinancialDecisionMetricName =
  | 'projectIrrPct'
  | 'paybackYears'
  | 'hurdleComparison';

export interface FinancialDecisionMetricUnavailability {
  metric: FinancialDecisionMetricName;
  reason:
    | 'cash-flows-do-not-cross-zero'
    | 'cash-flows-do-not-pay-back-within-project-life'
    | 'project-irr-unavailable';
}

export interface FinancialDecisionMetricAvailability {
  projectLifeYears: number;
  hurdleRatePct: number;
  projectIrrPct: number | null;
  paybackYears: number | null;
  irrToHurdleSpreadPct: number | null;
  unavailable: FinancialDecisionMetricUnavailability[];
}

const READY: ReportAuthorityReadiness = {
  ready: true,
  blockers: [],
  warnings: [],
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function positive(value: unknown): value is number {
  return isFiniteNumber(value) && value > 0;
}

function nonNegative(value: unknown): value is number {
  return isFiniteNumber(value) && value >= 0;
}

function inClosedRange(value: unknown, low: number, high: number): value is number {
  return isFiniteNumber(value) && value >= low && value <= high;
}

function validOptionalNonNegative(value: unknown): boolean {
  return value == null || nonNegative(value);
}

function validOptionalPercent(value: unknown): boolean {
  return value == null || inClosedRange(value, 0, 100);
}

function effectiveWaccPct(core: FinancialAssumptionsCore): number | null {
  if (isFiniteNumber(core.waccPct)) return core.waccPct;
  return deriveWaccPct(
    core.debtToEquityRatio,
    core.debtRatePct,
    core.equityHurdlePct,
    core.taxRatePct,
  );
}

function financialInputIssues(core: FinancialAssumptionsCore): string[] {
  const issues: string[] = [];
  if (core.technology === 'tbd') issues.push('technology');
  if (!positive(core.capacityMW)) issues.push('capacityMW');
  if (
    !positive(core.capacityFactorPct)
    || core.capacityFactorPct > 100
  ) {
    issues.push('capacityFactorPct');
  }
  if (
    !positive(core.projectLifeYears)
    || !Number.isInteger(core.projectLifeYears)
  ) {
    issues.push('projectLifeYears');
  }
  if (!positive(core.capexTotalUsd)) issues.push('capexTotalUsd');
  if (core.capexPerKwUsd != null && !positive(core.capexPerKwUsd)) {
    issues.push('capexPerKwUsd');
  }
  if (
    core.constructionMonths != null
    && (!positive(core.constructionMonths) || !Number.isInteger(core.constructionMonths))
  ) {
    issues.push('constructionMonths');
  }
  if (!validOptionalNonNegative(core.decommissioningUsd)) {
    issues.push('decommissioningUsd');
  }
  if (!validOptionalNonNegative(core.workingCapitalUsd)) {
    issues.push('workingCapitalUsd');
  }
  if (!validOptionalPercent(core.contingencyPct)) {
    issues.push('contingencyPct');
  }
  if (!nonNegative(core.fixedOmPerKwYearUsd)) {
    issues.push('fixedOmPerKwYearUsd');
  }
  if (
    core.variableOmPerMwhUsd != null
    && !nonNegative(core.variableOmPerMwhUsd)
  ) {
    issues.push('variableOmPerMwhUsd');
  }
  if (
    core.fuelCostPerMwhUsd != null
    && !nonNegative(core.fuelCostPerMwhUsd)
  ) {
    issues.push('fuelCostPerMwhUsd');
  }
  if (!validOptionalPercent(core.insuranceAndPropertyTaxPct)) {
    issues.push('insuranceAndPropertyTaxPct');
  }
  if (
    core.debtToEquityRatio != null
    && !inClosedRange(core.debtToEquityRatio, 0, 1)
  ) {
    issues.push('debtToEquityRatio');
  }
  if (
    core.debtRatePct != null
    && !inClosedRange(core.debtRatePct, 0, 100)
  ) {
    issues.push('debtRatePct');
  }
  if (
    core.equityHurdlePct != null
    && !inClosedRange(core.equityHurdlePct, 0, 100)
  ) {
    issues.push('equityHurdlePct');
  }
  if (!validOptionalPercent(core.taxRatePct)) issues.push('taxRatePct');
  if (!validOptionalPercent(core.itcPct)) issues.push('itcPct');
  if (!validOptionalNonNegative(core.ptcPerMwhUsd)) {
    issues.push('ptcPerMwhUsd');
  }
  if (!nonNegative(core.ppaPricePerMwhUsd)) {
    issues.push('ppaPricePerMwhUsd');
  }
  if (
    core.ppaEscalatorPctPerYear != null
    && (
      !isFiniteNumber(core.ppaEscalatorPctPerYear)
      || core.ppaEscalatorPctPerYear <= -100
    )
  ) {
    issues.push('ppaEscalatorPctPerYear');
  }
  if (
    core.ppaTermYears != null
    && (!positive(core.ppaTermYears) || !Number.isInteger(core.ppaTermYears))
  ) {
    issues.push('ppaTermYears');
  }
  if (
    core.inServiceDate != null
    && !Number.isFinite(Date.parse(core.inServiceDate))
  ) {
    issues.push('inServiceDate');
  }
  const wacc = effectiveWaccPct(core);
  if (!inClosedRange(wacc, 0, 100)) issues.push('waccPct');
  return issues;
}

/**
 * Financial report authority is the saved project financial model only.
 * A BOM, suppliers, quotes, pathways, and solved simulations can enrich a
 * report but can never make an incomplete financial model ready.
 */
export function evaluateFinancialReportReadiness(
  assumptions: FinancialAssumptions | null | undefined,
): ReportAuthorityReadiness {
  if (!assumptions) {
    return {
      ready: false,
      blockers: [{
        code: 'financial-model-missing',
        message: 'Add and save the project financial model before running Financial analysis.',
        resolver: 'financial-model',
      }],
      warnings: [],
    };
  }

  if (isDataCenterFinance(assumptions)) {
    const result = computeDataCenterFinance(assumptions.dataCenterModel);
    return result.computable
      ? { ready: true, blockers: [], warnings: [] }
      : { ready: false, blockers: [{ code: 'financial-model-incomplete', resolver: 'financial-model',
        message: `Complete the annual data-center model: ${result.issues.join(', ')}.`, fields: result.issues }], warnings: [] };
  }
  const core = canonicalCore(assumptions);
  const issues = financialInputIssues(core);
  if (
    assumptions.currency !== 'USD'
    && !positive(assumptions.exchangeRateToUsd)
  ) {
    issues.push('exchangeRateToUsd');
  }
  const metrics = issues.length === 0 ? recompute(core) : null;
  const hurdleRatePct = effectiveWaccPct(core);
  let decisionMetrics: FinancialDecisionMetricAvailability | undefined;
  if (
    metrics
    && (
      !isFiniteNumber(metrics.annualMwh)
      || !isFiniteNumber(metrics.lcoeNominalPerMwhUsd)
      || !isFiniteNumber(metrics.npvUsd)
    )
  ) {
    if (!isFiniteNumber(metrics.annualMwh)) issues.push('annualMwh');
    if (!isFiniteNumber(metrics.lcoeNominalPerMwhUsd)) {
      issues.push('lcoeNominalPerMwhUsd');
    }
    if (!isFiniteNumber(metrics.npvUsd)) issues.push('npvUsd');
  }
  /*
   * OUTPUTS ARE NOT INPUT GAPS (2026-08-19). This block used to push
   * `projectIrrPct` / `irrToHurdleSpreadPct` / `paybackYears` into `issues`,
   * which turned them into a `financial-model-incomplete` blocker reading
   * "Missing or invalid: paybackYears" and a Path-to-GO "Resolve inputs" CTA.
   *
   * Those three are ENGINE OUTPUTS, not model inputs. A project whose cash
   * flows never cross zero (undefined IRR) or never pay back within its life
   * is a perfectly computable, perfectly valid UNFINANCEABLE result — exactly
   * what the Financial analysis exists to state. Blocking on them meant the
   * worse the economics, the less likely any verdict existed at all (verified
   * against the `unfinanceableFa` fixture: NPV −$2.10B, IRR −0.29%, gate
   * `ready=false, fields=['paybackYears']`, while `buildFinancialAnalysis`
   * bands the SAME model `red / 18 / Unfinanceable`).
   *
   * The gate below still fails closed on genuine INPUT problems (missing or
   * out-of-range assumptions, currency without a rate, and the non-finite
   * `annualMwh` / `lcoeNominalPerMwhUsd` / `npvUsd` above, which mean the model
   * cannot be evaluated at all). Unavailable OUTPUTS are recorded as reason
   * codes on `financialDecisionMetrics.unavailable` and surfaced to the reader
   * instead of withheld behind a blocker.
   */
  if (metrics && isFiniteNumber(hurdleRatePct) && positive(core.projectLifeYears)) {
    const cashFlows = computeCashFlowsUsd(core);
    const unavailable: FinancialDecisionMetricUnavailability[] = [];
    if (!cashFlows) {
      // The cash-flow SERIES itself could not be built from the saved model —
      // an INPUT failure (nothing downstream is evaluable), so it still blocks.
      issues.push('cashFlows');
    }
    if (!isFiniteNumber(metrics.projectIrrPct)) {
      const crossesZero = cashFlows
        ? cashFlows.some(value => value < 0) && cashFlows.some(value => value > 0)
        : true;
      unavailable.push({
        metric: 'projectIrrPct',
        reason: crossesZero
          ? 'project-irr-unavailable'
          : 'cash-flows-do-not-cross-zero',
      });
      unavailable.push({
        metric: 'hurdleComparison',
        reason: 'project-irr-unavailable',
      });
    }
    if (!isFiniteNumber(metrics.paybackYears)) {
      unavailable.push({
        metric: 'paybackYears',
        reason: 'cash-flows-do-not-pay-back-within-project-life',
      });
    }
    const irrToHurdleSpreadPct = isFiniteNumber(metrics.projectIrrPct)
      ? metrics.projectIrrPct - hurdleRatePct
      : null;
    decisionMetrics = {
      projectLifeYears: core.projectLifeYears,
      hurdleRatePct,
      projectIrrPct: metrics.projectIrrPct,
      paybackYears: metrics.paybackYears,
      irrToHurdleSpreadPct,
      unavailable,
    };
  }

  const uniqueIssues = [...new Set(issues)];
  if (uniqueIssues.length > 0 || !metrics) {
    return {
      ready: false,
      blockers: [{
        code: 'financial-model-incomplete',
        message:
          `Complete the saved project financial model before running Financial analysis. `
          + `Missing or invalid: ${uniqueIssues.join(', ')}.`,
        resolver: 'financial-model',
        fields: uniqueIssues,
      }],
      warnings: [],
      ...(metrics ? { financialMetrics: metrics } : {}),
      ...(decisionMetrics ? { financialDecisionMetrics: decisionMetrics } : {}),
    };
  }

  return {
    ready: true,
    blockers: [],
    warnings: [],
    financialMetrics: metrics,
    financialDecisionMetrics: decisionMetrics,
  };
}

const FINANCIAL_DECISION_METRIC_GAP_TEXT: Record<
  FinancialDecisionMetricName,
  Record<FinancialDecisionMetricUnavailability['reason'], string>
> = {
  projectIrrPct: {
    'cash-flows-do-not-cross-zero':
      'Project IRR is undefined — the modelled cash flows never cross zero.',
    'cash-flows-do-not-pay-back-within-project-life':
      'Project IRR is undefined — the modelled cash flows never pay back.',
    'project-irr-unavailable':
      'Project IRR could not be solved from the modelled cash flows.',
  },
  paybackYears: {
    'cash-flows-do-not-cross-zero':
      'Payback is undefined — the modelled cash flows never cross zero.',
    'cash-flows-do-not-pay-back-within-project-life':
      'The project does not pay back within its modelled life.',
    'project-irr-unavailable':
      'Payback is unavailable for this cash-flow profile.',
  },
  hurdleComparison: {
    'cash-flows-do-not-cross-zero':
      'The IRR-to-hurdle spread is unavailable without a project IRR.',
    'cash-flows-do-not-pay-back-within-project-life':
      'The IRR-to-hurdle spread is unavailable without a project IRR.',
    'project-irr-unavailable':
      'The IRR-to-hurdle spread is unavailable without a project IRR.',
  },
};

/**
 * Render `financialDecisionMetrics.unavailable` for a reader.
 *
 * These reason codes used to be computed and then consumed by nothing while
 * the same conditions silently produced a "Resolve inputs" blocker. The model
 * is COMPLETE in these cases — NPV and LCOE are still reported — so the honest
 * statement is which decision metrics the economics cannot produce, not a
 * demand that the operator go fix inputs that are not broken.
 *
 * Returns null when nothing is unavailable.
 */
export function formatFinancialDecisionMetricGaps(
  decisionMetrics: FinancialDecisionMetricAvailability | null | undefined,
): string | null {
  const unavailable = decisionMetrics?.unavailable ?? [];
  if (unavailable.length === 0) return null;
  const sentences = [...new Set(unavailable.map(
    entry => FINANCIAL_DECISION_METRIC_GAP_TEXT[entry.metric][entry.reason],
  ))];
  return (
    `The saved project financial model is complete and was evaluated. `
    + `${sentences.join(' ')} `
    + `These are engine outputs, not missing inputs — the analysis reports the `
    + `resulting economics rather than withholding a verdict.`
  );
}

export function evaluateSupplyChainReportReadiness(
  plan: SupplyChainPlan | null | undefined,
): ReportAuthorityReadiness {
  const scopeLimitations = validateMaterialScopeLimitations(plan?.materialScopeLimitations);
  const reportableItems = plan?.bom.filter(isReportableUserBomItem) ?? [];
  if (!isSupplyChainPlanUserReady(plan ?? undefined) || reportableItems.length === 0) {
    return {
      ready: false,
      blockers: [{
        code: 'supply-chain-bom-missing',
        message:
          `Add or upload at least ${SUPPLY_CHAIN_MIN_REPORTABLE_BOM_ITEMS} valid equipment BOM items, `
          + 'including at least one marked critical or critical-path, before running '
          + 'Supply Chain Risk analysis.',
        resolver: 'equipment-bom',
      }],
      warnings: [],
    };
  }

  const warnings: Array<ReportAuthorityFinding<ReportAuthorityWarningCode>> = [];
  if (scopeLimitations.length > 0) {
    warnings.push({
      code: 'supply-chain-scope-incomplete',
      message: 'Some material scope remains unknown and excluded from the priced BOM. The subtotal is not a complete equipment budget; this gap cannot support low procurement risk.',
      resolver: 'equipment-bom',
    });
  }
  if (!plan || plan.suppliers.length === 0) {
    warnings.push({
      code: 'supply-chain-suppliers-missing',
      message:
        'No suppliers are linked to the equipment plan. The analysis may run but cannot conclude low procurement risk.',
      resolver: 'equipment-bom',
    });
  }
  if (reportableItems.some(
    item => !nonNegative(item.unitCostUsd) || !nonNegative(item.totalCostUsd),
  )) {
    warnings.push({
      code: 'supply-chain-pricing-incomplete',
      message:
        'Some BOM items are unpriced. The analysis may run but must disclose the pricing gap.',
      resolver: 'equipment-bom',
    });
  }
  if (!plan || plan.quotes.length === 0) {
    warnings.push({
      code: 'supply-chain-quotes-missing',
      message:
        'No vendor quotes are attached. The analysis may run but must disclose the quote gap.',
      resolver: 'equipment-bom',
    });
  }

  return {
    ready: true,
    blockers: [],
    warnings,
  };
}

const SUPPLY_CHAIN_CONFIDENCE_RISK_ID =
  'supply-chain-authority-evidence-gaps';
const SUPPLY_CHAIN_LOW_RISK_SCORE_CAP = 69;

/**
 * Missing commercial enrichment does not block Supply Chain Risk generation,
 * but it cannot support a green/low-risk conclusion. Stamp the exact warning
 * codes and add one deterministic execution risk so HTML and every exporter
 * read the same constrained result.
 */
export function applySupplyChainAuthorityConfidenceCap(
  report: SiteRagReport,
  readiness: ReportAuthorityReadiness,
): SiteRagReport {
  const warnings = readiness.warnings.filter(
    (warning): warning is ReportAuthorityFinding<SupplyChainAuthorityWarningCode> =>
      warning.code.startsWith('supply-chain-'),
  );
  const warningCodes = warnings.map(warning => warning.code);
  const uncappedOverallScore = report.overallScore;
  if (warnings.length === 0) {
    report.supplyChainAuthorityAtGen = {
      schemaVersion: 1,
      status: 'complete',
      warningCodes: [],
      lowRiskEligible: true,
      scoreCap: null,
      uncappedOverallScore,
    };
    return report;
  }

  if (
    Number.isFinite(report.overallScore)
    && report.overallScore > SUPPLY_CHAIN_LOW_RISK_SCORE_CAP
  ) {
    report.overallScore = SUPPLY_CHAIN_LOW_RISK_SCORE_CAP;
  }
  if (report.overallStatus === 'green') {
    report.overallStatus = 'amber';
  }
  report.supplyChainAuthorityAtGen = {
    schemaVersion: 1,
    status: 'limited',
    warningCodes,
    lowRiskEligible: false,
    scoreCap: SUPPLY_CHAIN_LOW_RISK_SCORE_CAP,
    uncappedOverallScore,
  };
  const triggeringValue = warningCodes.join(', ');
  report.risks = [
    ...(report.risks ?? []).filter(
      risk => risk.id !== SUPPLY_CHAIN_CONFIDENCE_RISK_ID,
    ),
    {
      id: SUPPLY_CHAIN_CONFIDENCE_RISK_ID,
      category: 'execution',
      label: 'Procurement evidence gaps',
      severity: warningCodes.includes('supply-chain-quotes-missing')
        || warningCodes.includes('supply-chain-suppliers-missing')
        ? 'high'
        : 'medium',
      impact:
        'Supplier, pricing, or quote gaps reduce procurement confidence; the available evidence cannot support a low-risk conclusion.',
      mitigation: warnings.map(warning => warning.message).join(' '),
      triggeringValue,
      source: 'evidence',
    },
  ];
  return report;
}

/**
 * STAGE 5.2 — Generation & Impact readiness.
 *
 * Modelled on `evaluateFinancialReportReadiness`: Financial is gated on a saved
 * financial model, and this report is gated on a PROMOTED generation scenario.
 *
 * "Saved" is deliberately NOT the gate. Promotion is recorded on ProjectIntent
 * (`promotedGenerationScenarioId` plus the two integrity hashes) and a draft
 * never sets it, so a site can have many solved scenarios and still not have
 * chosen one — and choosing is precisely what makes the downstream LCOE and
 * grid-vs-BTM assertions attributable.
 *
 * `resolvePromotedGenerationScenario` returns null when the id is absent OR
 * when either stamped hash no longer matches the snapshot, which separates the
 * two blockers: nothing promoted, versus promoted-then-invalidated. The second
 * matters because the report would otherwise be written against a basis the
 * site has already moved past.
 */
export function evaluateGenerationImpactReportReadiness(
  site: Partial<SiteEntity>,
): ReportAuthorityReadiness {
  if (site.generationAssessmentSelection?.kind === 'grid-led-standby-planning') {
    const reason = selectedGridLedPlanningReadinessReason(site);
    return reason ? { ready: false, blockers: [{ code: 'generation-scenario-stale', message: reason,
      resolver: 'generation-scenario', fields: ['generationAssessmentSelection'] }], warnings: [] }
      : { ready: true, blockers: [], warnings: [{ code: 'generation-planning-scope', message: 'Selected user-declared static planning; engineering, utility firmness, layout and EO qualification remain indeterminate.', resolver: 'generation-scenario' }] };
  }
  const intent = site.projectIntent;
  // FIX 2026-09-05 — GRID-ONLY must be refused HERE, not only in
  // `resolveAnalysisDecisionMateriality`.
  //
  // Materiality gave grid-only sites `prerequisiteReady: true` so the UI shows
  // "does not apply" rather than a blocked row — correct for planning. But the
  // enqueue chokepoint keyed on that flag and ACCEPTED the job, while
  // `runTypedReportGeneration` called this function, which demanded a promoted
  // scenario and threw. A grid-only site could therefore burn a job slot and
  // surface a red Failed row — precisely what the chokepoint exists to prevent
  // — and the invalidation reaper re-created that job on every SED or intent
  // edit. The two gates must agree, so applicability is evaluated here too.
  const applicability = assessGenerationImpactTestFitApplicability({
    projectIntent: intent,
    powerAvailabilityPreferences: site.typedPreferencesDefaults?.['power-availability'],
  });
  if (applicability.status === 'grid-only') {
    return {
      ready: false,
      blockers: [{
        code: 'generation-not-applicable',
        message:
          'This site declares a grid-only supply path, so Generation & Impact does not apply. '
          + 'Grid capacity, interconnection queue, tariffs and curtailment are covered by the '
          + 'BTM Feasibility report.',
        resolver: 'generation-scenario',
        fields: ['projectIntent.phases'],
      }],
      warnings: [],
    };
  }
  // P1.10 — grid-coded phases with the strategy still UNDECLARED are NOT
  // grid-only (see `assessGenerationImpactTestFitApplicability`): the report
  // stays required and is blocked on a promoted scenario like any other
  // undecided site, but the message names the input that is actually missing
  // — the declaration — rather than sending the owner of a probably-grid-only
  // site off to solve a generation scenario they may never need.
  if (applicability.status === 'undecided' && applicability.hasExplicitGridOnlyPhases) {
    return {
      ready: false,
      blockers: [{
        code: 'generation-scenario-missing',
        message:
          'Every declared phase is grid-supplied, but the accountable grid-versus-on-site power '
          + 'strategy has not been declared. Declare grid-only to record that Generation & Impact '
          + 'does not apply to this site, or declare hybrid / BTM-only / islanded and promote a solved '
          + 'generation scenario.',
        resolver: 'generation-scenario',
        fields: ['typedPreferencesDefaults.power-availability.btmStrategy'],
      }],
      warnings: [],
    };
  }
  if (!intent?.promotedGenerationScenarioId) {
    return {
      ready: false,
      blockers: [{
        code: 'generation-scenario-missing',
        message:
          'Promote a solved generation scenario before running Generation & Impact analysis.',
        resolver: 'generation-scenario',
        fields: ['promotedGenerationScenarioId'],
      }],
      warnings: [],
    };
  }
  const promoted = resolvePromotedGenerationScenario(
    intent,
    site.promotedGenerationScenarioSnapshot,
    null,
    site.generationAssessmentSelection,
    site.generationProjectInputBinding,
  );
  if (!promoted) {
    return {
      ready: false,
      blockers: [{
        code: 'generation-scenario-stale',
        message:
          'The promoted generation scenario no longer matches its recorded integrity stamps. '
          + 'Re-solve and re-promote it before running Generation & Impact analysis.',
        resolver: 'generation-scenario',
        fields: ['promotedGenerationScenarioId'],
      }],
      warnings: [],
    };
  }
  return READY;
}

export function evaluateReportAuthorityReadiness(
  reportType: ReportTypeId,
  site: Partial<SiteEntity>,
): ReportAuthorityReadiness {
  if (reportType === 'test-fit' && site.generationAssessmentSelection?.kind === 'grid-led-standby-planning') {
    return { ready: false, blockers: [{ code: 'generation-scenario-missing', message: 'Grid-led standby static planning does not qualify physical equipment layout or a Test Fit. Select a qualified engineering/layout assessment for this output.', resolver: 'generation-scenario', fields: ['generationAssessmentSelection'] }], warnings: [] };
  }
  if (reportType === 'financial') {
    return evaluateFinancialReportReadiness(site.financialAssumptions);
  }
  if (reportType === 'supply-chain-risk') {
    return evaluateSupplyChainReportReadiness(site.supplyChainPlan);
  }
  // STAGE 5.2 — the id stays `power-availability` (see 5.5: renaming a
  // persisted ReportTypeId is unprecedented and unsupported); only the LABEL
  // becomes "Generation & Impact Report".
  if (reportType === 'power-availability') {
    return evaluateGenerationImpactReportReadiness(site);
  }
  return READY;
}

export function formatReportAuthorityBlockers(
  readiness: ReportAuthorityReadiness,
): string {
  return readiness.blockers.map(blocker => blocker.message).join(' ');
}

export class ReportAuthorityPrerequisiteError extends Error {
  readonly code = 'REPORT_AUTHORITY_PREREQUISITE_BLOCKED';

  constructor(readonly readiness: ReportAuthorityReadiness) {
    super(formatReportAuthorityBlockers(readiness));
    this.name = 'ReportAuthorityPrerequisiteError';
  }
}

export function assertReportAuthorityReadiness(
  reportType: ReportTypeId,
  site: Partial<SiteEntity>,
): ReportAuthorityReadiness {
  const readiness = evaluateReportAuthorityReadiness(reportType, site);
  if (!readiness.ready) {
    throw new ReportAuthorityPrerequisiteError(readiness);
  }
  return readiness;
}
